import React, { useRef } from "react";
import { View, Text, TextInput, StyleSheet, Platform, SafeAreaView } from "react-native";
import { useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../state/src/useSessionStore";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";

type Props = {
  title?: string;
  subTitle?: string;
  pinError?: string;
  pin: string[];
  handleChange: (text: string, index: number) => void;
  inputsRef: React.MutableRefObject<(TextInput | null)[]>;
  attemptsLeft?: number;
  locked?: boolean;
};

const PinInputComponent: React.FC<Props> = ({
  title,
  subTitle,
  pinError,
  pin,
  handleChange,
  inputsRef,
  attemptsLeft,
  locked
}) => {
  const { t } = useTranslation();
  const isNewConsumer = useSessionStore((s: SessionState) => (s as any).isNewConsumer);
  const Wrapper = Platform.OS === "web" ? View : SafeAreaView;
  return (
    <Wrapper style={styles.container}>
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.subtitle}>{subTitle}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <TextInput
            key={index}
            testID={`pinInput${index}`}
            ref={(el) => (inputsRef.current[index] = el)}
            style={styles.pinInput}
            keyboardType="number-pad"
            maxLength={1}
            secureTextEntry
            value={digit}
            onChangeText={(text) => handleChange(text, index)}
          />
        ))}
      </View>

      {isNewConsumer ?
        pin.join("").length > 0 && pin.join("").length < 4 && (
        <Text style={styles.error}>{t("PIN_INPUT_ERROR")}</Text>
        ) : attemptsLeft === 3 ? (
          <View></View>
        ) : !locked ? (
        <Text style={styles.attempts}>
          {attemptsLeft} {attemptsLeft === 1 ? "attempt" : "attempts"} remaining
        </Text>
        ) : (
          <Text style={styles.locked}>{t("ACCOUNT_LOCKED")}</Text>
      )}
    </Wrapper>
  );
}

export default PinInputComponent;

const styles = StyleSheet.create({
  container: {
    justifyContent: "flex-start",
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  pinContainer: {
    flexDirection: "row",
    marginBottom: 10,
  },
  pinInput: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: "#F9FAFB",
    marginRight: 24
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
  attempts: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 23,
    color: Colors.errorRedColor,
    marginBottom: 37
  },
  locked: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 23,
    color: Colors.errorRedColor,
    marginBottom: 37
  },
});
